import {
  SetWttpTemplateAsDefaultDocument,
  UserWithOrganizationDocument,
  UserWithOrganizationQuery,
  WttpTemplate,
  WttpTemplatesDocument,
} from 'src/graphql/generated/graphql';
import { Confirmation } from '@otp/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Star } from 'src/components/common/icons';

type SetTemplateDefaultProps = {
  wttpTemplate: Partial<WttpTemplate>;
  onSubmitted?: () => void;
  isUserAdmin?: boolean;
  isGlobalTemplatesOnly: boolean;
};

export const SetTemplateDefault = ({
  wttpTemplate,
  onSubmitted,
  isUserAdmin,
  isGlobalTemplatesOnly,
}: SetTemplateDefaultProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'wttp.template.list.actions',
  });
  const [showWttpTemplateAsDefaultModal, setShowWttpTemplateAsDefaultModal] =
    useState(false);
  const isDefault = !!wttpTemplate.isDefault;
  // the main idea under these check is to mark the star icon for default templates only for admins or for OTPs but on "Custom" tab
  const isMarkIconSelected =
    isDefault && (isUserAdmin || !isGlobalTemplatesOnly);
  const isMarkRowAsSelected =
    isDefault && !isUserAdmin && isGlobalTemplatesOnly;

  const { data } = useQuery<UserWithOrganizationQuery>(
    UserWithOrganizationDocument,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    },
  );

  const storesUsedByDefaultTemplate =
    data?.currentUser?.organization?.defaultWttpTemplate?.usedByStoreCount;

  const [setWttpTemplateAsDefaultMutation] = useMutation(
    SetWttpTemplateAsDefaultDocument,
    {
      refetchQueries: [WttpTemplatesDocument],
    },
  );

  const setWttpTemplateAsDefault = async () => {
    try {
      await setWttpTemplateAsDefaultMutation({
        variables: {
          id: wttpTemplate.id,
        },
      });
      onSubmitted?.();
    } finally {
      setShowWttpTemplateAsDefaultModal(false);
    }
  };

  return (
    <>
      <Star
        isActive={isMarkIconSelected}
        onClick={() => setShowWttpTemplateAsDefaultModal(true)}
        data-role={isMarkRowAsSelected ? 'selected-row' : ''}
      />
      {showWttpTemplateAsDefaultModal && (
        <Confirmation
          open
          onClose={() => setShowWttpTemplateAsDefaultModal(false)}
          onConfirm={setWttpTemplateAsDefault}
          title={t('confirm-set-default.title', {
            storesCount: storesUsedByDefaultTemplate,
          })}
          message={t('confirm-set-default.message')}
        />
      )}
    </>
  );
};
